// Phase 5.2 — Vendors list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu + Tooltip in vendorList.tsx with
// the shared DataTable, StatusBadge, a DropdownMenu and shared Tooltip.
// Presentation only: search + server pagination + the Sort select reuse the
// screen's handlers; the row action menu (Edit / Active-Inactive toggle / Delete,
// keys "1"/"2"/"3") calls the screen's `onMenuAction(key, item)` unchanged.
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical, TrendingDown, TrendingUp, X } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Badge } from "ui/badge";
import { Button } from "ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AppConstant from "../../Globals/AppConstants";

const STATUS_OPTIONS = [
  { id: "1", name: "Active" },
  { id: "0", name: "Inactive" },
];

// Same current/previous-FY purchase + growth-badge convention as
// CustomerTable.tsx / VendorPurchaseTable.tsx — copied verbatim.
function PurchaseCell({ amount, count }: { amount: any; count: any }) {
  return (
    <div>
      <div className="tabular-nums text-foreground">₹{Number(amount ?? 0).toLocaleString("en-IN")}</div>
      <div className="text-[11px] text-muted-foreground">({Number(count ?? 0)} Purchases)</div>
    </div>
  );
}

function GrowthCell({ current, previous }: { current: any; previous: any }) {
  const currentAmount = Number(current ?? 0);
  const previousAmount = Number(previous ?? 0);

  let percent = 0;
  if (previousAmount === 0 && currentAmount > 0) percent = 100;
  else if (previousAmount === 0 && currentAmount === 0) percent = 0;
  else percent = ((currentAmount - previousAmount) / previousAmount) * 100;

  const rounded = Math.round(percent);
  const variant = rounded > 0 ? "success" : rounded < 0 ? "destructive" : "secondary";
  const Icon = rounded > 0 ? TrendingUp : TrendingDown;

  return (
    <Badge variant={variant} className="gap-1">
      {rounded !== 0 && <Icon className="h-3 w-3" />}
      {rounded > 0 ? "+" : ""}{rounded}%
    </Badge>
  );
}

const SORT_OPTIONS = [
  { id: "7", name: "Outstanding (High → Low)" },
  { id: "0", name: "Latest" },
  { id: "3", name: "Oldest" },
  { id: "1", name: "Name (A-Z)" },
  { id: "2", name: "Name (Z-A)" },
  { id: "5", name: "Purchase Amount (High → Low)" },
  { id: "6", name: "Purchase Amount (Low → High)" },
];

const Ellipsis = ({ text, width }: { text: any; width: string }) => {
  const value = text ? text : "-";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`${width} overflow-hidden text-ellipsis whitespace-nowrap`}>{value}</div>
        </TooltipTrigger>
        {text && <TooltipContent>{text}</TooltipContent>}
      </Tooltip>
    </TooltipProvider>
  );
};

export interface VendorTableProps {
  data: any[];
  loading: boolean;
  onMenuAction: (key: string, item: any) => void;
  onViewHistory: (item: any) => void;
  onViewDebts: (item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  sortBy?: any;
  onSortChange: (sortBy: any) => void;
  statusFilter?: string;
  onStatusFilterChange: (status: string) => void;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
  getRowClassName?: (row: any) => string | undefined;
}

export function VendorTable({
  data,
  loading,
  onMenuAction,
  onViewHistory,
  onViewDebts,
  searchValue,
  onSearch,
  searchPlaceholder,
  sortBy,
  onSortChange,
  statusFilter,
  onStatusFilterChange,
  title,
  headerActions,
  pagination,
  getRowClassName,
}: VendorTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "vendorCode", header: "ID", cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.vendorCode || "-"}</span>, meta: { sticky: "left" } },
      { id: "vendorName", header: "Vendor Name", cell: ({ row }) => <span className="whitespace-normal break-words">{row.original?.vendorName || "-"}</span>, meta: { sticky: "left" } },
      {
        id: "purchaseDate",
        header: "Purchase Date",
        cell: ({ row }) => (row.original?.lastPurchaseDate ? moment(row.original?.lastPurchaseDate).format("DD-MMM-YYYY") : "-"),
      },
      {
        id: "currentYear",
        header: "Current Year Purchase",
        cell: ({ row }) => <PurchaseCell amount={row.original?.currentYearAmount} count={row.original?.currentYearCount} />,
      },
      {
        id: "previousYear",
        header: "Previous Year Purchase",
        cell: ({ row }) => <PurchaseCell amount={row.original?.previousYearAmount} count={row.original?.previousYearCount} />,
      },
      {
        id: "purchaseGrowth",
        header: "Purchase Growth",
        cell: ({ row }) => <GrowthCell current={row.original?.currentYearAmount} previous={row.original?.previousYearAmount} />,
      },
      {
        id: "history",
        header: "Purchase History",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onViewHistory(row.original)}
            className="text-primary hover:underline"
          >
            {AppConstant?.view}
          </button>
        ),
      },
      {
        // Vendor Debt module — the unpaid amount still to be paid to this
        // vendor. Unrelated to the purchase columns above: this is cash
        // position, not stock. Value only, no navigation — the ledger is
        // reached from the separate Debt Ledger column.
        id: "vendorDebts",
        header: "Vendor Debts",
        cell: ({ row }) => {
          const outstanding = Number(row.original?.outstandingDebt ?? 0);
          return (
            <span className={`whitespace-nowrap tabular-nums font-medium ${outstanding > 0 ? "text-destructive" : "text-foreground"}`}>
              ₹{outstanding.toLocaleString("en-IN")}
            </span>
          );
        },
      },
      {
        // A bare View link into the ledger, in the style of CustomerTable.tsx's
        // "Credit History" column. Unlike that one this is shown for EVERY
        // vendor, including those with no transactions yet — the ledger screen
        // is where debts are entered, so hiding it would leave no way to record
        // a vendor's first debt.
        id: "debtLedger",
        header: "Debt Ledger",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onViewDebts(row.original)}
            className="text-primary hover:underline"
          >
            {AppConstant?.view}
          </button>
        ),
      },
      { id: "contactName", header: "Contact Name", cell: ({ row }) => <Ellipsis text={row.original?.contactName} width="w-[100px]" /> },
      { id: "mobileNumber", header: "Mobile Number", cell: ({ row }) => (row.original?.mobileNumber ? row.original?.mobileNumber : "-") },
      { id: "emailId", header: "Email", cell: ({ row }) => <Ellipsis text={row.original?.emailId} width="w-[160px]" /> },
      { id: "websiteUrl", header: "Website", cell: ({ row }) => <Ellipsis text={row.original?.websiteUrl} width="w-[150px]" /> },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original?.isActive == 1 ? "Active" : "Inactive"} />,
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onMenuAction("1", item)}>{AppConstant?.edit}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMenuAction("2", item)}>{item?.isActive == 1 ? AppConstant?.InActive : AppConstant?.Active}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMenuAction("3", item)}>{AppConstant?.Delete}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        meta: { sticky: "right" },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const sortToolbar = (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <Select value={statusFilter || undefined} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {statusFilter && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Clear status filter"
            onClick={() => onStatusFilterChange("")}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <span className="text-sm text-muted-foreground">Sort</span>
      <Select value={sortBy != null ? String(sortBy) : "0"} onValueChange={(v) => onSortChange(v)}>
        <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      toolbar={sortToolbar}
      title={title}
      headerActions={headerActions}
      pagination={pagination}
      getRowClassName={getRowClassName}
    />
  );
}

export default VendorTable;
